import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AIHelperService } from '@cr-driver/ai/ai.helper';
import { AIStore } from '@cr-driver/ai/ai.store';
import { AuthHandler } from '@cr-driver/handler/auth.handler';
import { DriverStore } from '@cr-driver/stores/driver.store';
import { IamStore } from '@cr-driver/stores/iam.store';
import { environment } from '@cr-environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { PcIconComponent } from '@shared/components/pc-icon/pc-icon.component';
import { marked } from 'marked';

@Component({
  selector: 'app-wattson',
  imports: [FormsModule, MatButtonModule, MatIconModule, TranslateModule, PcIconComponent],
  templateUrl: './wattson.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './wattson.component.scss',
})
export class WattsonComponent implements AfterViewChecked {
  private readonly authHandler = inject(AuthHandler);
  private readonly aiHelper = inject(AIHelperService);
  private readonly driverStore = inject(DriverStore);
  private readonly iamStore = inject(IamStore);
  private readonly host = inject(ElementRef<HTMLElement>);
  protected readonly aiStore = inject(AIStore);
  protected readonly isEnabled = environment.wattson;
  protected readonly isLoggedIn = toSignal(this.authHandler.loggedIn, { initialValue: false });
  private readonly chargePoint = toSignal(this.driverStore.chargePoint$, { initialValue: null });
  private readonly configurationCode = toSignal(this.driverStore.configurationCode$, {
    initialValue: null,
  });
  private readonly organization = toSignal(this.iamStore.activeCROrganization$, {
    initialValue: null,
  });

  protected readonly isOpen = signal(false);
  protected readonly showLabel = signal(true);
  protected inputText = '';

  private readonly hideLabelAfterDelay = effect((onCleanup) => {
    if (!this.isEnabled || !this.isLoggedIn()) {
      return;
    }

    const timer = window.setTimeout(() => this.showLabel.set(false), 2000);
    onCleanup(() => window.clearTimeout(timer));
  });

  @ViewChild('messageList') messageList!: ElementRef<HTMLElement>;

  constructor() {
    this.keepInsideVisualViewport();
  }

  private keepInsideVisualViewport(): void {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const sync = () => {
      const shift = viewport.offsetLeft;
      this.host.nativeElement.style.transform = shift ? `translate3d(${-shift}px, 0, 0)` : '';
    };

    viewport.addEventListener('resize', sync);
    viewport.addEventListener('scroll', sync);
    inject(DestroyRef).onDestroy(() => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
    });
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  protected renderMarkdown(text: string): string {
    return marked.parse(text ?? '', { async: false }) as string;
  }

  private scrollToBottom(): void {
    const el = this.messageList?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  sendMessage(): void {
    const question = this.inputText.trim();
    if (!question || this.aiStore.isLoading()) return;
    this.aiStore.sendMessage(question, {
      userId: this.authHandler.getUserProfile()?.user_id,
      chargePointKind: this.aiHelper.chargePointKind(this.chargePoint() ?? null),
      chargePointName: this.aiHelper.chargePointName(this.configurationCode() ?? null),
      pricePolicyCurrent: this.aiHelper.pricePolicy(this.organization()?.price_policy?.active),
      pricePolicyUpcoming: this.aiHelper.pricePolicy(this.organization()?.price_policy?.upcoming),
    });
    this.inputText = '';
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }
}
